// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useMemo, useCallback } from "react";
import Plot from "react-plotly.js";
import { TimeSeriesData } from "../../lib/models";

interface BudgetImpactTimelineProps {
  data: TimeSeriesData[];
  timeHorizon?: number;
  title?: string;
  discountingEnabled?: boolean;
  discountRatePct?: number;
}

/**
 * Budget Impact Timeline showing yearly costs with and without vaccine programme
 * Displays dual-line chart with shaded fill between series
 */

// Helper to check if a value is a finite number
const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

export const BudgetImpactTimeline: React.FC<BudgetImpactTimelineProps> = ({
  data,
  timeHorizon,
  title = "Budget impact timeline",
  discountingEnabled = false,
  discountRatePct = 3,
}) => {

  const paddedRows = useMemo(() => {
    if (!data.length) {
      return [];
    }
    if (!timeHorizon || timeHorizon < 1) {
      return data;
    }

    const totalRows = timeHorizon + 1; // include Year 0 baseline
    const lastRow = data[data.length - 1];

    return Array.from({ length: totalRows }, (_, idx) => {
      return data[idx] ?? lastRow;
    });
  }, [data, timeHorizon]);

  // Apply start-of-year discounting: disc(y) = 1 / (1 + r)^(y - 1)
  // Only applies when discounting_enabled = TRUE and timeHorizon > 1
  const applyDiscounting = useCallback((value: number | null, yearIndex: number): number | null => {
    if (value === null || !isFiniteNumber(value)) return value;
    if (!discountingEnabled || !timeHorizon || timeHorizon <= 1) return value;

    const discountRate = discountRatePct / 100;
    const year = yearIndex; // Year 0 = baseline, Year 1 = first costed year
    if (year === 0) return value;

    // Start-of-year discounting: Year 1 undiscounted, Year 2 = 1/(1+r), etc.
    const discountFactor = 1 / Math.pow(1 + discountRate, year - 1);
    return value * discountFactor;
  }, [discountingEnabled, discountRatePct, timeHorizon]);

  const { xAxisLabels, xAxisValues, costWithVaccineSeries, costWithoutVaccineSeries, hasCostData } = useMemo(() => {
    const rows = paddedRows.length ? paddedRows : data;
    
    // Build cost series separately - use null for missing values (Plotly.js handles null as missing points)
    const hasCostWithout = rows.some((r) => isFiniteNumber(r.costWithoutVaccine));
    const hasCostWith = rows.some((r) => isFiniteNumber(r.costWithVaccine));

    const costWithoutVaccine = hasCostWithout
      ? rows.map((r, idx) => {
          const value = isFiniteNumber(r.costWithoutVaccine) ? r.costWithoutVaccine : null;
          return applyDiscounting(value, idx);
        })
      : [];

    // costWith: prefer explicit; otherwise derive ONLY if we have costWithout available for that row
    const costWithVaccine = (hasCostWith || hasCostWithout)
      ? rows.map((r, idx) => {
          let value: number | null = null;
          
          if (isFiniteNumber(r.costWithVaccine)) {
            value = r.costWithVaccine;
          } else if (isFiniteNumber(r.costWithoutVaccine)) {
            // derive only when inputs are present
            const savings = isFiniteNumber(r.savingsEUR) ? r.savingsEUR : 0;
            const programmeCost = isFiniteNumber(r.programmeCost) ? r.programmeCost : 0;
            value = programmeCost + Math.max(0, r.costWithoutVaccine - savings);
          }
          
          return applyDiscounting(value, idx);
        })
      : [];
    
    return {
      xAxisLabels: rows.map((_, idx) => `Year ${idx}`),
      xAxisValues: rows.map((_, idx) => idx),
      costWithVaccineSeries: costWithVaccine,
      costWithoutVaccineSeries: costWithoutVaccine,
      hasCostData: hasCostWithout || hasCostWith,
    };
  }, [data, paddedRows, applyDiscounting]);

  const plotData = useMemo(() => {
    // Only show cost data - no fallbacks to case data or estimates
    if (!hasCostData) {
      return [] as Plotly.Data[];
    }

    // Use consistent colors: blue for baseline (without vaccine), red/green for comparison (with vaccine)
    // Red (#f87171) when costs are higher, green (#34d399) when costs are lower (savings)
    // Filter out null values for average calculation
    const validWithVaccine = costWithVaccineSeries.filter((v): v is number => v !== null);
    const validWithoutVaccine = costWithoutVaccineSeries.filter((v): v is number => v !== null);
    const avgWithVaccine = validWithVaccine.length > 0 
      ? validWithVaccine.reduce((a, b) => a + b, 0) / validWithVaccine.length 
      : 0;
    const avgWithoutVaccine = validWithoutVaccine.length > 0
      ? validWithoutVaccine.reduce((a, b) => a + b, 0) / validWithoutVaccine.length
      : 0;
    const withVaccineIsHigher = avgWithVaccine > avgWithoutVaccine;

    const traces: Plotly.Data[] = [
      {
        x: xAxisValues,
        y: costWithoutVaccineSeries,
        type: "scatter",
        mode: "lines+markers",
        name: "Without vaccine programme",
        line: { color: "#4C8DFF", width: 2 }, // Standardized blue for baseline
      },
      {
        x: xAxisValues,
        y: costWithVaccineSeries,
        type: "scatter",
        mode: "lines+markers",
        name: "With vaccine programme",
        line: { color: withVaccineIsHigher ? "#f87171" : "#34d399", width: 2 }, // Red when higher, green when lower (savings)
      },
    ];

    return traces;
  }, [hasCostData, costWithVaccineSeries, costWithoutVaccineSeries, xAxisValues]);

  const axisMax =
    xAxisValues.length > 0 ? xAxisValues[xAxisValues.length - 1] : 1;

  // Calculate y-axis range with padding
  // Only use cost data - no fallbacks to case data
  // Filter out null values for min/max calculation
  const yValues = hasCostData 
    ? [...costWithVaccineSeries, ...costWithoutVaccineSeries].filter((v): v is number => v !== null)
    : [];
  const yMin = yValues.length > 0 ? Math.min(...yValues) : 0;
  const yMax = yValues.length > 0 ? Math.max(...yValues) : 1;
  const yRange = yMax - yMin;
  
  // Calculate appropriate tick spacing
  const calculateTickSpacing = (range: number): number => {
    if (range <= 0) return 1;
    const rough = Math.ceil(range / 6);
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
    const candidates = [1, 2, 5, 10].map((c) => c * magnitude);
    return candidates.find((c) => rough <= c) ?? candidates[candidates.length - 1];
  };
  
  const dtick = calculateTickSpacing(yRange);
  // Ensure 0 is always included in the y-axis range
  // Calculate min/max that includes 0 and all data points
  let yAxisMin = Math.floor(yMin / dtick) * dtick;
  let yAxisMax = Math.ceil(yMax / dtick) * dtick;
  
  // Ensure 0 is always displayed - adjust range to include 0
  if (yAxisMin > 0) {
    // All values are positive, extend range down to include 0
    yAxisMin = Math.floor(0 / dtick) * dtick - dtick;
  } else if (yAxisMax < 0) {
    // All values are negative, extend range up to include 0
    yAxisMax = Math.ceil(0 / dtick) * dtick + dtick;
  } else {
    // Range spans zero, ensure zero is included with padding
    yAxisMin = Math.min(Math.floor(yMin / dtick) * dtick - dtick, -dtick);
    yAxisMax = Math.max(Math.ceil(yMax / dtick) * dtick + dtick, dtick);
  }

  const layout: Partial<Plotly.Layout> = {
    // title: { 
    //   text: title, 
    //   font: { size: 16, color: '#e2e8f0', family: 'Inter, sans-serif' } 
    // },
    xaxis: {
      tickmode: "array",
      tickvals: xAxisValues,
      ticktext: xAxisLabels,
      tick0: 0,
      dtick: 1,
      range: [0, axisMax],
      tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
      gridcolor: '#334155', // Lighter gray for better visibility
      showgrid: true, // Enable grid marks for better readability
      gridwidth: 1,
      griddash: 'dot', // Use dotted lines for subtle grid
      ticklen: 8, // Increase tick length for better visibility
      tickwidth: 1,
    },
    yaxis: {
      title: { text: "Annual cost (EUR)", font: { color: '#e2e8f0', family: 'Inter, sans-serif' } },
      tickformat: ",.2s", // Use SI notation (K, M, B) for better readability
      range: [yAxisMin, yAxisMax],
      dtick: dtick,
      tickmode: 'linear', // Use linear tick mode
      tick0: 0, // Ensure ticks start at 0 (or align to include 0)
      tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
      gridcolor: '#334155', // Lighter gray for better visibility
      showgrid: true, // Enable grid marks for better readability
      gridwidth: 1,
      griddash: 'dot', // Use dotted lines for subtle grid
      zeroline: true,
      zerolinecolor: '#94a3b8', // Visible gray color for zero line
      zerolinewidth: 2, // Make it clearly visible
      fixedrange: false, // Allow zooming
      ticklen: 8, // Increase tick length for better visibility
      tickwidth: 1,
      ticklabelposition: 'outside', // Position tick labels outside to increase spacing from origin
    },
    showlegend: true,
    legend: {
      font: { color: '#e2e8f0', family: 'Inter, sans-serif' },
      x: 0.98, // Position legend inside plot area, near right edge
      xanchor: 'right', // Anchor to right edge of legend
      y: 1, // Top of plot
      yanchor: 'top', // Anchor to top of legend
      bgcolor: 'rgba(15, 23, 42, 0.85)', // Semi-transparent background for readability
      bordercolor: '#334155',
      borderwidth: 1,
    },
    margin: { l: 90, r: 20, t: 50, b: 60 }, // Increased left and bottom margins for better spacing at origin
    autosize: true,
    hovermode: "x unified",
    hoverlabel: {
      bgcolor: 'rgba(15, 23, 42, 0.95)',
      bordercolor: '#334155',
      font: {
        color: '#e2e8f0',
        family: 'Inter, sans-serif',
        size: 12,
      },
    },
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    font: { color: '#e2e8f0', family: 'Inter, sans-serif' },
  };

  const config: Partial<Plotly.Config> = {
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ["pan2d", "lasso2d", "select2d"],
  };

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
      </div>
      <div className="vaccine-demo__chart" style={{ position: 'relative' }}>
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: "100%", height: "400px" }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

